<template>
  <div v-if="hasAssets" class="py-6">
    <div class="flex items-start justify-between">
      <LabelValue class="pr-8" :label="t('Reviews.product.view.assets.title').toString()">
        <template #hint>
          {{ t('Reviews.product.view.assets.description') }}
        </template>
      </LabelValue>
      <OpenProductProfile class="shrink-0" :slug="product.slug" />
    </div>
    <SponsoredContentBanner class="mb-8" data-testid="sponsored-content-banner" />
    <LabelValue :label="t('Reviews.product.view.screenshots.label').toString()" data-testid="product-screenshots">
      <div v-if="hasImages" class="grid grid-cols-3 gap-6">
        <AssetPreview
          v-for="(screenshot, index) in product.screenshots"
          :key="screenshot.url"
          :content-type="screenshot.contentType"
          :name="screenshot.name"
          :url="screenshot.url"
          :data-testid="`screenshot-${index}`"
        />
      </div>
      <span v-else>{{ t('Reviews.product.view.screenshots.missing') }}</span>
    </LabelValue>
    <LabelValue :label="t('Reviews.product.view.documents.label').toString()" data-testid="product-documents">
      <div v-if="documentsFenced">
        {{ t('Reviews.product.view.documents.notActive') }}
      </div>
      <div v-else>
        <div v-if="hasDocuments" class="grid grid-cols-4 gap-6">
          <DocumentCard
            v-for="doc in productDocuments"
            :key="doc.url"
            :content-type="doc.contentType"
            :name="doc.name"
            :size="doc.size"
            :preview-url="doc.previewUrl || undefined"
            data-testid="document-card"
            @click="downloadFile(doc.url)"
          />
        </div>
        <span v-else>{{ t('Reviews.product.view.documents.missing') }}</span>
      </div>
    </LabelValue>
  </div>
  <ReviewsEmptyView
    v-else
    class="py-8"
    :title="t('Reviews.product.view.assets.noAssetsTitle')"
    :description="t('Reviews.product.view.assets.noAssetsInfo')"
    :action="{
      label: t('Reviews.product.view.actions.addAssets'),
      to: {
        name: ReviewsRouteName.ReviewsProductEditAssets,
        params: { productId: product.id },
      },
    }"
  >
    <template #image>
      <div class="rounded-full bg-surface-elevation-5 p-3">
        <NoAssetsIcon />
      </div>
    </template>
  </ReviewsEmptyView>
</template>

<script setup lang="ts">
import { DocumentCard } from '@ramp106/omrjs-reviews-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import NoAssetsIcon from '@/components/ReviewsCommon/ReviewsAssetsEdit/NoAssetsIcon.vue'
import ReviewsEmptyView from '@/components/ReviewsEmptyView.vue'
import OpenProductProfile from '@/components/ReviewsManagement/OpenProductProfile.vue'
import SponsoredContentBanner from '@/components/ReviewsManagement/SponsoredContentBanner.vue'
import type { ProductData } from '@/gql/reviews'
import { ALLOWED_LOCALES, mapTranslationsArrToObj } from '@/helpers/translationsHelper'
import { ReviewsRouteName } from '@/router/types'
import AssetPreview from '@/ui/AssetPreview.vue'
import LabelValue from '@/ui/LabelValue.vue'

const props = defineProps<{
  product: ProductData
}>()

const { t, locale } = useI18n()

const hasImages = computed(() => props.product.screenshots.length > 0)
const hasDocuments = computed(() => props.product.documents.length > 0)
const hasAssets = computed(() => hasImages.value || hasDocuments.value)

const documentsFenced = computed(() => {
  return !props.product.configFlags.documentsEditable
})

const productDocuments = computed(() =>
  props.product.documents.map((doc) => {
    const nameTranslationsMap = mapTranslationsArrToObj(doc.nameTranslations)
    const name = nameTranslationsMap[locale.value] || nameTranslationsMap[ALLOWED_LOCALES[0]]
    return { ...doc, name }
  }),
)

function downloadFile(url: string) {
  window.open(url, '_blank')
}
</script>
